<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <ColumnToggle
          :table-name="'queryPathMaps'"
          :user-settings="userSettings"
          :settings-status="getUserSettingsStatus"
          :column-settings="columnSettings"
          @onChange="updateUserVisibleColumns"
        />
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('PLACEHOLDER_URL_PARAMETER_SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!loading"
      class="m-b-15"
    >
      <button
        type="button"
        class="btn btn-success btn-sm"
        :disabled="showCreateForm"
        @click="displayCreateForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'ACTION_CREATE_URL_PARAMETER'" />
      </button>
    </div>
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="safeParameters"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              v-for="col in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :default-sort="columnSettings[col].defaultSort"
              :sort-key="columnSettings[col].name"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <tr>
            <td
              v-if="emptyTable"
              v-t="'URL_PARAMETERS_TABLE_EMPTY'"
              colspan="100%"
            />
          </tr>
          <tr
            v-if="showCreateForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <UrlParameterForm
                :show-loader="createQueryParamStatus.status === 'pending'
                  || updateQueryParamStatus.status === 'pending'"
                :is-new-url-parameter="true"
                @cancel="hideCreateForm"
                @submit="checkForDuplicatesAndCreateUrlParameter"
              />
            </td>
          </tr>
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
            >
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showCreateForm"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <td
                v-if="userVisibleColumns.includes('incomingQueryStringName')"
                class="clickable"
                @click="displayEditForm(row.id)"
              >
                {{ row.incomingQueryStringName }}
              </td>
              <td v-if="userVisibleColumns.includes('mapToTagType')">
                {{ row.mapToTagType }}
              </td>
              <td v-if="userVisibleColumns.includes('mapToTagName')">
                {{ row.mapToTagName }}
              </td>
              <td v-if="!isMobile">
                <UrlParametersActions
                  :current-url-parameter="row"
                  :disabled="disabled"
                  :get-references-status="getReferencesStatus"
                  :delete-parameter-status="deleteParameterStatus"
                  :url-param-references="references"
                  @edit="displayEditForm"
                  @open-delete="getUrlReferences"
                  @confirm-delete="deleteUrlParameter"
                />
              </td>
            </tr>
            <tr
              v-if="showEditForm(row.id)"
              :key="row.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <UrlParameterForm
                  :show-loader="createQueryParamStatus.status === 'pending'
                    || updateQueryParamStatus.status === 'pending'"
                  :current-url-parameter="row"
                  :is-new-url-parameter="false"
                  @cancel="hideEditForm"
                  @submit="checkForDuplicatesAndUpdateUrlParameter"
                />
              </td>
            </tr>
            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <UrlParametersActions
                  :current-url-parameter="row"
                  :disabled="disabled"
                  :get-references-status="getReferencesStatus"
                  :delete-parameter-status="deleteParameterStatus"
                  :url-param-references="references"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @edit="displayEditForm"
                  @open-delete="getReferences"
                  @confirm-delete="deleteUrlParameter"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'URL_PARAMETERS_TABLE_LOADING'" />
    </div>
  </PortalWidget>
</template>

<script>
import PortalWidget from '@/core/components/PortalWidget.vue';
import UrlParametersActions from './UrlParametersActions';
import UrlParameterForm from './UrlParameterForm';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle';
import messages from '../locales';
import Loader from '@/core/components/Loader';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import { urlParameterService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';

export default {
  name: 'UrlParameters',
  components: {
    Loader,
    UrlParameterForm,
    UrlParametersActions,
    PortalWidget,
    ColumnToggle
  },
  props: { },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      currentUrlParameter: { },
      isNewUrlParameter: true,
      showCreateForm: false,
      isMobile: isMobile,
      activeActionRowId: null,
      activeEditRowId: null,
      activeUrlParamReferences: null,
      widget: {
        title:this.$i18n.t('HEADER_URL_PARAMETERS'),
        iconClass: 'typcn typcn-code'
      },
      filters: {
        name: { value: '', keys: ['incomingQueryStringName','mapToTagType','mapToTagName'] }
      },
    };
  },
  computed: {
    ...mapState('urlParameters', [
      'urlParameters',
      'references',

      'userVisibleColumns',
    ]),
    ...mapState('ui', [
      'userSettings'
    ]),
    ...mapGetters('urlParameters', [
      'getParametersStatus',
      'createQueryParamStatus',
      'updateQueryParamStatus',
      'getReferencesStatus',
      'deleteParameterStatus',
      'getUserVisibleColumnsStatus',
    ]),
    ...mapGetters('ui', [
      'getUserSettingsStatus',
    ]),
    loading() {
      return this.getParametersStatus.status == STATUS_PENDING;
    },
    emptyTable() {
      return this.getParametersStatus.status == STATUS_SUCCESS
      && this.safeParameters
      && this.safeParameters.length == 0;
    },
    safeParameters() {
      if(this.getParametersStatus.status == STATUS_SUCCESS){
        return this.urlParameters;
      }
      return [];
    },
    columnSettings() {
      return urlParameterService.columnSettings;
    },
    disabled() {
      return this.createQueryParamStatus.status == STATUS_PENDING
      || this.updateQueryParamStatus.status == STATUS_PENDING
      || this.showCreateForm
      || !!this.activeEditRowId;
    }
  },
  watch: {
    currentPage() {
      this.hideEditForm();
      this.showCreateForm = false;
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.hideEditForm();
        this.showCreateForm = false;
      }
    }
  },
  created() {
    this.getParameters();
    this.getUserSettings();
  },
  methods: {
    ...mapActions('urlParameters', [
      'getParameters',
      'createQueryParam',
      'updateQueryParam',
      'getReferences',
      'deleteParameter',
      'getUserVisibleColumns'
    ]),
    ...mapActions('ui', [
      'getUserSettings'
    ]),
    updateUserVisibleColumns() {
      this.getUserVisibleColumns();
    },
    displayCreateForm() {
      this.hideEditForm();
      this.showCreateForm = true;
    },
    hideCreateForm() {
      this.showCreateForm = false;
    },
    displayEditForm(id) {
      this.hideCreateForm();
      this.activeEditRowId = id;
    },
    hideEditForm() {
      this.activeEditRowId = null;
    },
    showEditForm(rowId) {
      return !this.showCreateForm && !this.showEditRowId && this.activeEditRowId == rowId;
    },
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    async getUrlReferences(id) {
      return await this.getReferences(id);
    },
    checkForDuplicateURLParams(urlParameter) {
      const duplicate = this.urlParameters.find(param => {
        return param.incomingQueryStringName === urlParameter.incomingQueryStringName &&
          param.mapToTagType === urlParameter.mapToTagType &&
          param.mapToTagName === urlParameter.mapToTagName &&
          param.id !== urlParameter.id
      });
      return duplicate;
    },
    async checkForDuplicatesAndCreateUrlParameter(urlParameter) {
      const duplicate = this.checkForDuplicateURLParams(urlParameter);
      if (duplicate) {
        utilities.showToaster(STATUS_ERROR, this.$i18n.t('MESSAGE_DUPLICATE_URL_PARAMETER'));
        return;
      }
      await this.createUrlParameter(urlParameter);
    },
    async createUrlParameter(urlParameter) {
      await this.createQueryParam(urlParameter);
      if (this.createQueryParamStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_CREATE_SUCCESS'));
        this.hideCreateForm();
      }
      if (this.createQueryParamStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createQueryParamStatus.error);
      }
    },
    async checkForDuplicatesAndUpdateUrlParameter(urlParameter) {
      const duplicate = this.checkForDuplicateURLParams(urlParameter);
      if (duplicate) {
        utilities.showToaster(STATUS_ERROR, this.$i18n.t('MESSAGE_DUPLICATE_URL_PARAMETER'));
        return;
      }
      await this.updateUrlParameter(urlParameter);
    },
    async updateUrlParameter(urlParameter) {
      await this.updateQueryParam(urlParameter);
      if (this.updateQueryParamStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_UPDATE_SUCCESS'));
        this.hideEditForm();
      }
      if (this.updateQueryParamStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updateQueryParamStatus.error);
      }
    },
    async deleteUrlParameter(id) {
      await this.deleteParameter(id);
      if (this.deleteParameterStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_DELETE_SUCCESS'));
      }
      if (this.deleteParameterStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.deleteParameterStatus.error);
      }
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
